import React, { useState, useEffect } from 'react';
import { Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { postApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState({ email: '', password: '' });
    const navigate = useNavigate();
    const [loader, setLoader] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const REMEMBER_ME_KEY = 'loginRememberMe';
    const REMEMBERED_EMAIL_KEY = 'loginRememberedEmail';
    const REMEMBERED_PASSWORD_KEY = 'loginRememberedPassword';

    const [rememberMe, setRememberMe] = useState(() => {
        try {
            return localStorage.getItem(REMEMBER_ME_KEY) === 'true';
        } catch {
            return false;
        }
    });

    // Restore email and password from localStorage when "Remember me" was previously checked
    useEffect(() => {
        if (rememberMe) {
            try {
                const savedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
                if (savedEmail) setEmail(savedEmail);
                const savedPassword = localStorage.getItem(REMEMBERED_PASSWORD_KEY);
                if (savedPassword) setPassword(savedPassword);
            } catch (e) {
                console.warn('Could not read remembered credentials:', e);
            }
        }
    }, []);

    useEffect(() => {
        // If user is already logged in, redirect to admin dashboard (prevents back button from showing login)
        const token = localStorage.getItem('token');
        if (token) {
            const userDetails = JSON.parse(localStorage.getItem('userDetail') || '{}');
            const role = userDetails.role ?? userDetails.role_id;
            // Role 2 is admin in this app; redirect to admin dashboard
            if (role === '2' || role === 2) {
                navigate('/admin/dashboard', { replace: true });
            } else if (userDetails.role === '1' || userDetails.role === 1) {
                navigate('/user/dashboard', { replace: true });
            } else {
                navigate('/admin/dashboard', { replace: true });
            }
        }
    }, [navigate]);

    const handleLogin = async () => {
        // Clear previous errors
        setErrors({ email: '', password: '' });
        
        // Validate form
        if (!validateForm()) {
            return;
        }

        setLoader(true);
            try {
                const insertData = { 
                email: email.trim(), 
                    password,
                role: 2 // Admin role
                };
            
            const response = await postApi('/api/v1/login', insertData, {}, false);
            
            // Check for successful response
            if (response?.statusCode === 200 || response?.statusCode === 201) {
                    // Handle different response structures
                const userData = response?.user_details || response?.data || response?.user || response;
                const token = userData?.token || response?.token || response?.accessToken || response?.access_token;
                    
                if (!token) {
                    setLoader(false);
                    toast.error('Login failed: Authentication token not received');
                    return;
                }
                
                // Store user data and token
                if (userData) {
                        localStorage.setItem("userDetail", JSON.stringify(userData));
                } else {
                    // If no user data, create a minimal user object
                    localStorage.setItem("userDetail", JSON.stringify({ email: email }));
                }
                
                // Store token (handle both JSON string and plain string)
                try {
                        localStorage.setItem("token", JSON.stringify(token));
                } catch (e) {
                    localStorage.setItem("token", token);
                }

                // Remember me: save or clear email and password in localStorage
                try {
                    if (rememberMe) {
                        localStorage.setItem(REMEMBER_ME_KEY, 'true');
                        localStorage.setItem(REMEMBERED_EMAIL_KEY, email.trim());
                        localStorage.setItem(REMEMBERED_PASSWORD_KEY, password);
                    } else {
                        localStorage.removeItem(REMEMBER_ME_KEY);
                        localStorage.removeItem(REMEMBERED_EMAIL_KEY);
                        localStorage.removeItem(REMEMBERED_PASSWORD_KEY);
                    }
                } catch (e) {
                    console.warn('Could not save remember me preference:', e);
                }
                
                        setLoader(false);
                toast.success(response?.message || 'Login successful!');
                
                // Small delay before navigation for better UX
                setTimeout(() => {
                        navigate('/admin/dashboard');
                }, 500);
                    } else {
                        setLoader(false);
                // Handle non-200 status codes
                const errorMessage = response?.message || response?.error || 'Login failed. Please try again.';
                
                // Check for specific field errors
                if (response?.errors) {
                    const fieldErrors = response.errors;
                    if (fieldErrors.email) {
                        setErrors(prev => ({ ...prev, email: fieldErrors.email }));
                    }
                    if (fieldErrors.password) {
                        setErrors(prev => ({ ...prev, password: fieldErrors.password }));
                    }
                } else {
                    toast.error(errorMessage);
                }
                }
            } catch (error) {
            setLoader(false);
                console.error('Login error:', error);
            
            // Handle specific error cases
            if (error?.response?.status === 401) {
                setErrors({ 
                    email: 'Invalid email or password', 
                    password: 'Invalid email or password' 
                });
                toast.error('Invalid email or password. Please check your credentials and try again.');
            } else if (error?.response?.status === 400) {
                const errorData = error.response?.data;
                if (errorData?.errors) {
                    const fieldErrors = errorData.errors;
                    setErrors({
                        email: fieldErrors.email || '',
                        password: fieldErrors.password || ''
                    });
                }
                toast.error(errorData?.message || 'Invalid input. Please check your email and password.');
            } else if (error?.code === 'ECONNABORTED') {
                toast.error('Request timeout. Please check your internet connection and try again.');
            } else if (!error?.response) {
                toast.error('Unable to connect to server. Please check your internet connection.');
            }
            // Other errors are already handled by postApi
        }
    };

    const validateForm = () => {
        const validationErrors = {};
        const trimmedEmail = email.trim();
        
        if (!trimmedEmail) {
            validationErrors.email = 'Email is required.';
        } else if (!/\S+@\S+\.\S+/.test(trimmedEmail)) {
            validationErrors.email = 'Please enter a valid email address.';
        }
        
        if (!password) {
            validationErrors.password = 'Password is required.';
        } else if (password.length < 6) {
            validationErrors.password = 'Password must be at least 6 characters long.';
        }
        
        setErrors(validationErrors);
        
        return Object.keys(validationErrors).length === 0;
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <>
            <div className='sign-up-main login'>
                <div className='sign-right'>
                    <div 
                        className='login-bg-image'
                        style={{
                            backgroundImage: `url(${window.getPublicUrl('/assets/images/login-bg.png')})`
                        }}
                    >
                    </div>
                </div>
                <div className='sign-left'>
                    <div className="login-card">
                        <h2>Login</h2>
                        <h4>Enter your credentials to access the dashboard</h4>
                        <form onSubmit={(e) => {
                            e.preventDefault();
                            handleLogin();
                        }}>
                            <div className="login-form-group">
                                <label>Email</label>
                                <input
                                    type="email"
                                    placeholder="Ex-Johndow342@gmail.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === ' ') {
                                            e.preventDefault();
                                        }
                                    }}
                                    required
                                />
                                {errors.email && <div className="error">{errors.email}</div>}
                            </div>

                            <div className="login-form-group login-password-group">
                                <label>Password</label>
                                <div className="password-input-wrapper">
                                    <input
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Enter your password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === ' ') {
                                                e.preventDefault();
                                            }
                                        }}
                                        required
                                    />
                                    <span className="eye-icon" onClick={togglePasswordVisibility}>
                                        <img src={showPassword ? window.getPublicUrl("/assets/images/Eye.svg") : window.getPublicUrl("/assets/images/Eye-closed.svg")} alt="Toggle Password" />
                                    </span>
                                </div>
                                {errors.password && <div className="error">{errors.password}</div>}
                            </div>

                            <div className="login-options">
                                <label className="rem">
                                    Remember me
                                    <input 
                                        type="checkbox" 
                                        checked={rememberMe}
                                        onChange={(e) => setRememberMe(e.target.checked)}
                                    />
                                    <span className="checkmark"></span>
                                </label>
                                <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
                            </div>

                            <Button variant="primary" className="login-signin-btn" onClick={handleLogin} disabled={loader}>
                                {loader && <div className="spinner-border spinner-border-sm" role="status"><span className="sr-only"></span></div>}
                                Sign In
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Login;
